import React from 'react';
import ReactDOM from 'react-dom/client';
import App from './App';
import ErrorBoundary from './components/ErrorBoundary';
import { DevErrorProvider } from './lib/dev-errors';
import './app/globals.css';
import './styles/table-fix.css';
import './styles/map-fullscreen.css';
import './styles/time-inputs.css';
import 'mapbox-gl/dist/mapbox-gl.css';

/**
 * Recover from a lazy chunk that will not load.
 *
 * A tab open across a deploy still holds the previous build's module graph, so
 * navigating to a route whose chunk is no longer served fails the dynamic import
 * and React Router renders "Unexpected Application Error". Reloading fetches a
 * fresh index.html — which is served `max-age=0, must-revalidate` — and with it
 * the current asset URLs.
 *
 * Reload at most once per tab: if the chunk is genuinely broken rather than
 * merely stale, a second attempt would loop, so let the error surface instead.
 */
const CHUNK_RELOAD_KEY = 'chunk-reload-attempted';

window.addEventListener('vite:preloadError', (event) => {
    if (sessionStorage.getItem(CHUNK_RELOAD_KEY)) return;
    event.preventDefault();
    sessionStorage.setItem(CHUNK_RELOAD_KEY, '1');
    window.location.reload();
});

// Got here, so the current build's entry executed: clear the guard so a future
// stale-chunk failure in this tab can reload again.
sessionStorage.removeItem(CHUNK_RELOAD_KEY);

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <ErrorBoundary>
      {import.meta.env.DEV ? (
        <DevErrorProvider>
          <App />
        </DevErrorProvider>
      ) : (
        <App />
      )}
    </ErrorBoundary>
  </React.StrictMode>
);
